import { Skeleton } from "@/components/ui/skeleton";
import { useSelectInputById } from "@/features/inputs/api/selectInputById";
import { cn } from "@/lib/utils";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

export interface ScaleRatingEntryValueProps
  extends Omit<BaseEntryValueProps, "value"> {
  value: number | null;
  inputId: string;
  showValue?: boolean;
}

export function ScaleRatingEntryValue({
  value,
  inputLabel,
  inputId,
  showValue = true,
  size,
  ...props
}: ScaleRatingEntryValueProps) {
  const { data: input, isLoading } = useSelectInputById(inputId);

  if (isLoading) {
    return (
      <Skeleton
        className={cn(
          "w-24 h-5",
          size === "sm" && "w-20 h-4",
          size === "lg" && "w-28 h-6",
        )}
      />
    );
  }

  if (!input?.scale_options || value === null) {
    return (
      <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
        <span
          className={cn(
            size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-base",
          )}
        >
          {value ?? "None"}
        </span>
      </BaseEntryValue>
    );
  }

  // Parse scale options if it's a string
  const scaleOptions = typeof input.scale_options === "string"
    ? JSON.parse(input.scale_options)
    : input.scale_options;

  // Find the option with matching value
  const selectedOption = Array.isArray(scaleOptions)
    ? scaleOptions.find(
      (option: { value: number; label: string }) =>
        option.value === Number(value),
    )
    : null;

  return (
    <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
      <div className="flex items-center gap-1">
        {showValue && value !== undefined && (
          <span
            className={cn(
              "flex items-center justify-center font-medium border border-slate-200 rounded-md aspect-square px-3 py-1 mr-2",
              size === "sm" ? "text-md" : size === "lg" ? "text-xl" : "text-lg",
            )}
          >
            {value}
          </span>
        )}
        {selectedOption?.label && (
          <span
            className={cn(
              "text-slate-700 dark:text-slate-200",
              size === "sm"
                ? "text-sm"
                : size === "lg"
                ? "text-lg"
                : "text-base",
            )}
          >
            {`${selectedOption.label}`}
          </span>
        )}
      </div>
    </BaseEntryValue>
  );
}
